/**
 * useCaDerDetection - Hook for detecting CA DER charges
 *
 * Handles automatic detection of California DER (Distributed Energy Resources)
 * charges based on location, tariff, and system configuration.
 *
 * CA DER charges include:
 * - Departing Load Charges (for self-generation)
 * - Standby Charges (for backup generation)
 *
 * @module hooks/useCaDerDetection
 */

import { useEffect } from 'react';
import { debug } from '../utils/debug';
import { detectCADERCharges, isCaliforniaIOU } from '../services/caDerService';

/**
 * Map utility names from tariff data to utility codes for CA DER
 * @param {string} utilityName - Utility name from tariff
 * @returns {string|null} Utility code (PGE, SCE, SDGE) or null
 */
function mapUtilityNameToCode(utilityName) {
    if (!utilityName) return null;
    const name = utilityName.toLowerCase();
    if (name.includes('pacific gas') || name.includes('pg&e') || name.includes('pge')) {
        return 'PGE';
    }
    if (name.includes('southern california edison') || name.includes('sce') || name.includes('edison')) {
        return 'SCE';
    }
    if (name.includes('san diego') || name.includes('sdge') || name.includes('sdg&e')) {
        return 'SDGE';
    }
    return null;
}

/**
 * @typedef {Object} CaDerDetectionOptions
 * @property {Object} scenarioForm - The scenario form state
 * @property {Object} currentUser - Current authenticated user
 */

/**
 * Hook for CA DER charge detection
 *
 * Runs detection when location, equipment, or tariff changes.
 * Uses tariff utility as source of truth when available.
 *
 * @param {CaDerDetectionOptions} options
 */
export function useCaDerDetection({ scenarioForm, currentUser }) {
    useEffect(() => {
        const runDetection = async () => {
            const lat = scenarioForm.tariffLocation?.lat;
            const lon = scenarioForm.tariffLocation?.lon;

            // Skip if no valid California location
            if (!lat || !lon || !isCaliforniaIOU(lat, lon)) {
                // Clear config if location changes to non-California
                if (scenarioForm.caDerConfig) {
                    debug.log('🔌 [useCaDerDetection] Location not in CA IOU territory - clearing CA DER config');
                    scenarioForm.setCaDerConfig(null);
                }
                return;
            }

            // Build scenario for detection
            const scenario = {
                solar_kw: parseFloat(scenarioForm.pvMaxKw) || 0,
                battery_kw: parseFloat(scenarioForm.storageMaxKw) || 0,
                battery_kwh: parseFloat(scenarioForm.storageMaxKwh) || 0,
                generator: scenarioForm.useGenerator ? {
                    capacity_kw: parseFloat(scenarioForm.generatorMaxKw) || 0,
                    fuel_type: scenarioForm.generatorFuelType || 'diesel'
                } : null
            };

            // Skip if no onsite generation
            const hasGeneration = scenario.solar_kw > 0 ||
                (scenario.generator && scenario.generator.capacity_kw > 0);
            if (!hasGeneration) {
                if (scenarioForm.caDerConfig) {
                    debug.log('🔌 [useCaDerDetection] No onsite generation - clearing CA DER config');
                    scenarioForm.setCaDerConfig(null);
                }
                return;
            }

            // Get utility from tariff (source of truth) - prioritize custom tariff, then selected tariff
            const effectiveTariff = scenarioForm.customTariffData || scenarioForm.selectedTariffData;
            const tariffUtilityName = effectiveTariff?.utility_name || effectiveTariff?.urdb_utility_name;
            const tariffUtilityCode = mapUtilityNameToCode(tariffUtilityName);

            debug.log('🔌 [useCaDerDetection] Tariff utility lookup:', {
                tariffUtilityName,
                tariffUtilityCode,
                hasTariff: !!effectiveTariff
            });

            try {
                debug.log('🔌 [useCaDerDetection] Running CA DER detection...', { lat, lon, scenario });
                const result = await detectCADERCharges(scenario, { latitude: lat, longitude: lon }, currentUser);

                if (result.has_onsite_generation) {
                    // NO FALLBACK: Tariff utility is REQUIRED - per CLAUDE.md no silent fallbacks
                    if (!tariffUtilityCode) {
                        debug.error('🔌 [useCaDerDetection] CA DER charges require a tariff with utility info. Please select a tariff.');
                        // Don't set caDerConfig without a valid utility - this will prevent incorrect calculations
                        return;
                    }

                    if (tariffUtilityCode !== result.detected_utility) {
                        debug.log('🔌 [useCaDerDetection] Tariff utility differs from geographic detection:', {
                            tariffUtility: tariffUtilityCode,
                            geoDetected: result.detected_utility
                        });
                    }

                    // Calculate generator capacity for standby (only dispatchable generation)
                    // Supports both multi-generator array and legacy single-generator field
                    let generatorKw = 0;
                    if (scenarioForm.useGenerator) {
                        const generators = scenarioForm.generators || [];
                        if (generators.length > 0) {
                            // Multi-generator: sum all generator capacities
                            generatorKw = generators.reduce((sum, g) => sum + (parseFloat(g.kw) || 0), 0);
                        } else {
                            // Legacy single-generator fallback
                            generatorKw = parseFloat(scenarioForm.generatorMaxKw) || 0;
                        }
                    }

                    const newConfig = {
                        detected_utility: result.detected_utility, // Keep geographic detection for reference only
                        has_onsite_generation: true,
                        technologies: result.technologies,
                        total_generation_kw: result.total_generation_kw,
                        generator_kw: generatorKw, // Standby is based on generator capacity only
                        title_17_compliant: result.title_17_compliant, // For D.21-07-011 eligibility display
                        config: {
                            planned_cod_year: result.suggested_cod_year,
                            system_availability: result.estimated_availability,
                            system_capacity_factor: result.estimated_capacity_factor,
                            islanding_capable: true,
                            voltage_level: effectiveTariff?.voltage_level || effectiveTariff?.rate_data?.voltage_level,
                        },
                        calculated: {
                            // Tariff utility is the ONLY source of truth - no fallback
                            effective_utility: tariffUtilityCode,
                            tariff_utility: tariffUtilityCode,
                            geo_detected_utility: result.detected_utility, // For reference/debugging only
                            standby_suspension_eligible: result.standby_suspension_eligible,
                            standby_suspension_ineligible_reason: result.standby_suspension_ineligible_reason,
                            final_standby_apply: result.standby_charges_apply,
                            final_departing_load_apply: result.departing_load_charges_apply,
                        },
                        // Preserve existing overrides if user has set them
                        ca_der_overrides: scenarioForm.caDerConfig?.ca_der_overrides || {},
                    };

                    // Only update if config actually changed (avoid infinite loops)
                    const existingConfig = scenarioForm.caDerConfig;
                    const needsUpdate = !existingConfig ||
                        existingConfig.calculated?.effective_utility !== newConfig.calculated.effective_utility ||
                        existingConfig.total_generation_kw !== newConfig.total_generation_kw ||
                        existingConfig.generator_kw !== newConfig.generator_kw || // Standby depends on generator capacity
                        existingConfig.calculated?.final_standby_apply !== newConfig.calculated.final_standby_apply ||
                        existingConfig.calculated?.final_departing_load_apply !== newConfig.calculated.final_departing_load_apply ||
                        existingConfig.config?.voltage_level !== newConfig.config.voltage_level; // Required for standby charges

                    if (needsUpdate) {
                        debug.log('🔌 [useCaDerDetection] CA DER detection complete - updating config:', {
                            effectiveUtility: newConfig.calculated.effective_utility,
                            tariffUtility: newConfig.calculated.tariff_utility,
                            geoDetected: newConfig.calculated.geo_detected_utility,
                            standby_apply: newConfig.calculated.final_standby_apply,
                            departing_load_apply: newConfig.calculated.final_departing_load_apply,
                            voltage_level: newConfig.config.voltage_level,
                        });
                        scenarioForm.setCaDerConfig(newConfig);
                    }
                }
            } catch (error) {
                debug.error('🔌 [useCaDerDetection] CA DER detection failed:', error);
            }
        };

        runDetection();
    }, [
        scenarioForm.tariffLocation?.lat,
        scenarioForm.tariffLocation?.lon,
        scenarioForm.pvMaxKw,
        scenarioForm.storageMaxKw,
        scenarioForm.storageMaxKwh,
        scenarioForm.useGenerator,
        scenarioForm.generatorMaxKw,
        scenarioForm.generatorFuelType,
        scenarioForm.generators, // Multi-generator array support
        scenarioForm.customTariffData?.utility_name,
        scenarioForm.selectedTariffData?.utility_name,
        currentUser
    ]);
}

export default useCaDerDetection;
